/**
 * Secondary G2 stream page content: semantics + FAQ + decision-support data
 * plus the thin builder that runs the shared generator and attaches them.
 */
import { buildStreamPageContent } from './secondaryStreamGenerator';
import type { DecisionProofPoint, FaqItem, LevelPageContent } from './types';

const G2_SEMANTICS: Record<string, any> = {
  keySuffix: 'G2',
  streamName: 'G2 stream',
  seoLabel: 'G2',
  eyebrowLabel: 'G2 MATHEMATICS',
  lower: {
    seoDescription:
      'The MOE G2 Mathematics syllabus for Lower Secondary, topic by topic — plus weekly live classes that strengthen algebra, geometry, and data skills.',
    headline:
      'Strengthen core concepts and prepare confidently for progression.',
    subheadFocus:
      'balanced conceptual understanding across algebra, geometry, and data handling.',
    syllabusBody:
      'Teaching balances conceptual clarity with steady progression so students stay secure as complexity increases.',
    syllabusTopics: [
      {
        category: 'Core Number & Algebra',
        items: [
          'Rational numbers and operations in context',
          'Algebraic manipulation and linear equations',
          'Ratio, proportion, and real-world applications',
        ],
      },
      {
        category: 'Geometry & Graphical Reasoning',
        items: [
          'Geometric properties and constructions',
          'Mensuration and coordinate-based problem solving',
          'Interpreting and using linear graphs',
        ],
      },
      {
        category: 'Statistics, Probability & Application',
        items: [
          'Data representation and interpretation',
          'Statistical measures and comparison',
          'Probability fundamentals in contextual questions',
        ],
      },
    ],
    syllabusNote:
      'Lower Secondary G2 students benefit most from consistent practice that builds understanding before upper-level exam preparation.',
    challengeFocusTitle: 'Keeping pace as abstraction increases',
    challengeFocusDescription:
      'Students can fall behind when routine methods become less effective and questions demand stronger reasoning.',
    streamSupportTitle: 'Targeted support to secure progression',
    streamSupportDescription:
      'We identify chapter-level weaknesses early and reinforce them before they affect future Upper Secondary topics.',
    progressionGoal:
      'enter Upper Secondary ready for more demanding applications and exam pathways.',
  },
  upper: {
    seoDescription:
      'The MOE G2 Mathematics syllabus for Upper Secondary, topic by topic — plus weekly live classes and strategic O-Level and N-Level exam preparation.',
    headline: 'Build exam-ready understanding for major secondary pathways.',
    subheadFocus:
      'applying core techniques consistently in mixed-topic, exam-style questions.',
    syllabusBody:
      'Revision integrates concept consolidation with exam execution so students can perform under timed conditions.',
    syllabusTopics: [
      {
        category: 'Upper Secondary Algebra & Functions',
        items: [
          'Linear and quadratic relationships',
          'Equation solving across mixed contexts',
          'Function interpretation and graph application',
        ],
      },
      {
        category: 'Geometry, Trigonometry & Mensuration',
        items: [
          'Geometric reasoning for exam scenarios',
          'Coordinate geometry in multi-step problems',
          'Trigonometric application and measurement',
        ],
      },
      {
        category: 'Exam Strategy & Topic Integration',
        items: [
          'Combining concepts across chapters',
          'Error analysis and correction routines',
          'Timed paper strategy and pacing control',
        ],
      },
    ],
    syllabusNote:
      'Upper Secondary G2 preparation focuses on consistent application and strategic revision for reliable exam performance.',
    challengeFocusTitle: 'Balancing exam demands across subjects',
    challengeFocusDescription:
      'With competing revision priorities, students often struggle to maintain depth and consistency in Math preparation.',
    streamSupportTitle: 'High-impact revision planning',
    streamSupportDescription:
      'DeepThink prioritises the topics and question types that most improve performance in exam settings.',
    progressionGoal:
      'perform confidently in major examinations with a clear, repeatable strategy.',
  },
};

const G2_FAQS: Record<string, FaqItem[]> = {
  '1-g2': [
    {
      question: 'What topics does Sec 1 G2 Math cover?',
      answer:
        'Sec 1 G2 covers rational numbers, algebraic manipulation, linear equations, geometric properties, mensuration, and basic data handling — at a pace that balances depth with steady progression.',
    },
    {
      question: 'How does G2 Math differ from G3 in Secondary 1?',
      answer:
        'G2 covers similar topics to G3 but with more scaffolding and practice time. G3 moves faster and introduces extensions earlier. The core syllabus overlap means transitions between streams are manageable.',
    },
    {
      question: 'Should my Sec 1 G2 child aim to move to G3?',
      answer:
        'It depends on your child. If they are consistently comfortable and scoring well, a move to G3 may be appropriate. If they are still building confidence, consolidating G2 foundations serves them better long-term.',
    },
    {
      question: 'What makes Sec 1 a critical year for G2 students?',
      answer:
        'Sec 1 is the transition from primary arithmetic to algebraic thinking. G2 students who do not secure these foundations in Sec 1 often find Sec 2 topics significantly harder, as every new concept builds on Sec 1 knowledge.',
    },
    {
      question: 'Is one weekly tuition class enough for Sec 1 G2?',
      answer:
        'For most students, one focused 1.5-hour class per week plus targeted online practice between sessions is sufficient. The key is consistency — regular engagement matters more than volume.',
    },
    {
      question: 'What is the SEC exam and how does it affect G2 students?',
      answer:
        'The Singapore-Cambridge SEC replaces all three national secondary exams (O-Level, N(A)-Level, N(T)-Level) from 2027. Students sit one unified exam with each subject at G1, G2, or G3 level — and can mix levels across subjects. G2 Math aligns to the former N(A) standard. The content and skills remain similar, but the unified framework means strong G2 foundations now are essential.',
    },
  ],
  '2-g2': [
    {
      question: 'What topics does Sec 2 G2 Math cover?',
      answer:
        "Sec 2 G2 covers algebraic manipulation, quadratic expressions, simultaneous equations, geometric properties, coordinate geometry, Pythagoras' theorem, trigonometric ratios, probability, and statistical analysis.",
    },
    {
      question: 'Is Sec 2 the right time to decide between G2 and G3?',
      answer:
        'Sec 2 performance often determines Upper Secondary stream placement. If your child is consistently strong, a move to G3 broadens exam options (including A-Math). Consistent G2 performance also prepares well for the SEC exam at the G2 level.',
    },
    {
      question: 'How does Sec 2 G2 prepare for Upper Secondary exams?',
      answer:
        'Sec 2 G2 builds the algebraic, geometric, and data-handling foundations that Upper Secondary exam topics directly extend. Weak Sec 2 understanding in any of these areas creates compounding difficulty in Sec 3-4.',
    },
    {
      question:
        "My child's marks are borderline — should they stay in G2 or try G3?",
      answer:
        'Borderline students benefit most from securing their G2 foundations first. A solid G2 base can still lead to strong SEC exam results, while a premature G3 move with shaky foundations often causes marks to drop further.',
    },
    {
      question: 'What should parents focus on during Sec 2 G2?',
      answer:
        'Focus on algebra and equation-solving fluency — these are the gateway skills for every Upper Secondary topic. If your child is comfortable with these by end of Sec 2, the transition to Sec 3 is much smoother.',
    },
    {
      question:
        'What is the SEC exam and how does it replace O-Levels for G2 students?',
      answer:
        'The Singapore-Cambridge SEC replaces all three national secondary exams (O-Level, N(A)-Level, N(T)-Level) from 2027 with one unified exam. G2 Math aligns to the former N(A) standard. Students can mix G-levels across subjects and receive a single certificate. The content and rigour remain similar — G2 students still need strong algebra, geometry, and data-handling skills.',
    },
  ],
  '3-g2': [
    {
      question: 'What does Sec 3 G2 Math cover?',
      answer:
        'Sec 3 G2 covers quadratic equations and functions, simultaneous equations in context, coordinate geometry, trigonometry applications, mensuration of complex shapes, and statistical analysis — all at exam-application level.',
    },
    {
      question: 'Does Sec 3 G2 prepare for O-Level or N-Level?',
      answer:
        'G2 students can prepare for either the O-Level or N(A)-Level examination depending on school assessment. Sec 3 content builds the skills required for both exam pathways.',
    },
    {
      question: 'How much harder is Sec 3 G2 compared to Sec 2?',
      answer:
        'The jump is significant. Sec 3 expects students to apply concepts in unfamiliar contexts, combine multiple topics in single questions, and work with greater precision. Students who were comfortable in Sec 2 often find Sec 3 challenging.',
    },
    {
      question: 'Should my Sec 3 G2 child be doing exam papers already?',
      answer:
        'Sec 3 is for building topic mastery, not exam drilling. Students benefit more from deep understanding of each chapter first, with exam-style practice ramping up in Sec 4 when the full syllabus is complete.',
    },
    {
      question:
        'My child wants to take O-Level E-Math — is G2 sufficient preparation?',
      answer:
        'G2 provides solid E-Math preparation when complemented with consistent practice and targeted gap-closing. Many G2 students achieve strong E-Math grades with the right support in Sec 3-4.',
    },
    {
      question: 'Will my Sec 3 G2 child sit the SEC exam or O-Levels?',
      answer:
        'Current Sec 3 students are the first cohort sitting the Singapore-Cambridge SEC in 2027 — a single national exam replacing all three former leaving exams. G2 Math aligns to the former N(A) standard. All subjects appear on one unified certificate, and students can mix G-levels across subjects.',
    },
  ],
  '4-g2': [
    {
      question:
        'What exams does a Sec 4 G2 / Normal Academic student prepare for?',
      answer:
        'G2 (Normal Academic) students may sit the N(A)-Level or O-Level Mathematics examination, depending on school assessment and progression. Some students sit both N(A)-Level and O-Level E-Math.',
    },
    {
      question:
        "Is it too late to improve my child's Math grade in Sec 4 G2 / NA?",
      answer:
        'Sec 4 is challenging but not too late. Focused revision on high-yield topics, consistent exam practice, and error analysis can produce real improvement — especially if weaknesses are clearly identified early in the year.',
    },
    {
      question:
        'What topics should Sec 4 G2 / Normal Academic students prioritise for revision?',
      answer:
        'Focus on algebra (equations and graphs), trigonometry, and mensuration — these topics carry the most marks in both N(A) and O-Level papers. Secure these and then work outward to remaining topics.',
    },
    {
      question:
        'Should my Sec 4 G2 / NA child attempt the O-Level E-Math paper?',
      answer:
        'If your child is consistently scoring well in school assessments, attempting O-Level E-Math broadens their post-secondary options significantly. Discuss eligibility with the school and assess readiness based on mock exam performance.',
    },
    {
      question: 'How should revision be structured in the Sec 4 exam year?',
      answer:
        'Start with topic-by-topic consolidation in Term 1-2, then shift to full paper practice in Term 3-4. Error tracking throughout the year ensures the same mistakes are not repeated in the actual exam.',
    },
  ],
};

const G2_DECISION: Record<string, DecisionProofPoint[]> = {
  '1-g2': [
    {
      title: 'Your child was comfortable in primary Math but is now struggling',
      description:
        'The transition to secondary-level abstraction — variables, equations, formal reasoning — catches many previously strong students off guard. Early support prevents a confidence spiral.',
    },
    {
      title: 'School test results are inconsistent',
      description:
        'Scoring well on some topics but poorly on others in Sec 1 G2 usually indicates uneven foundations. Targeted diagnostics identify which specific concepts need reinforcement.',
    },
    {
      title: 'Your child avoids Math homework or leaves questions blank',
      description:
        'Avoidance is a reliable signal that the student feels stuck but does not know how to ask for help. Weekly structured support provides a consistent space to work through difficulties.',
    },
  ],
  '2-g2': [
    {
      title: "Your child's Sec 1 gaps are affecting Sec 2 performance",
      description:
        'Sec 2 G2 topics build directly on Sec 1 foundations. If algebra, equations, or ratios from Sec 1 are shaky, every new Sec 2 topic becomes harder than it needs to be.',
    },
    {
      title: 'Streaming or subject combination decisions are approaching',
      description:
        'Sec 2 performance often determines Upper Secondary stream placement and subject options. Consistent support now can influence these important decisions positively.',
    },
    {
      title: 'Your child is aiming for O-Level E-Math eligibility',
      description:
        'G2 students who demonstrate strong and sustained improvement may be considered for O-Level E-Math. Building exam-ready skills in Sec 2 makes this pathway more achievable.',
    },
  ],
  '3-g2': [
    {
      title: 'The Sec 3 content jump has caught your child off guard',
      description:
        'Sec 3 G2 demands application-level thinking that is significantly harder than Sec 2. Students who were previously comfortable often need support adjusting to the increased complexity and pace.',
    },
    {
      title:
        'Your child needs to decide between N(A)-Level and O-Level pathways',
      description:
        'Sec 3 performance helps determine which exam your child should target. Consistent support ensures they are prepared for whichever pathway is most appropriate.',
    },
    {
      title: 'Lower Secondary gaps are making Upper Secondary topics harder',
      description:
        'If your child struggles with quadratic equations, trigonometry, or coordinate geometry, it is usually because prerequisite skills from Sec 1-2 are not secure. We diagnose and fix these alongside current topics.',
    },
  ],
  '4-g2': [
    {
      title:
        'Your Normal Academic / G2 child is preparing for N(A)-Level or O-Level and needs focused support',
      description:
        'The Sec 4 exam year requires structured revision that balances breadth (covering the full syllabus) with depth (mastering high-yield topics). Without a plan, students often revise unevenly.',
    },
    {
      title: 'Mock exam results are lower than expected',
      description:
        'Mock exams often reveal gaps that classroom learning missed. Analysing mock papers and targeting specific weak areas is more productive than generic revision.',
    },
    {
      title: 'Your child loses marks to avoidable errors in exam conditions',
      description:
        'Errors under pressure are usually a practice problem, not a knowledge problem. Timed practice with systematic error tracking reduces these losses significantly.',
    },
  ],
};

export function buildSecondaryG2Content(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const content = buildStreamPageContent(
    secLevel,
    G2_SEMANTICS,
    'g2',
    brandName
  );
  const dataKey = `${secLevel}-g2`;
  return {
    ...content,
    ...(G2_FAQS[dataKey] && { faqItems: G2_FAQS[dataKey] }),
    ...(G2_DECISION[dataKey] && {
      decisionProofPoints: G2_DECISION[dataKey],
    }),
  };
}
